import { useRef, type ReactElement, type ReactNode } from "react";
import { pixelType } from "odyssey-shared";
import { ArtIcon, type IconArt } from "./ArtIcon.tsx";
import { Glyph, type GlyphName } from "./Glyph.tsx";
import { useCentred } from "./measure.ts";
import { GLYPH_SIZES, centreOffset, logical } from "./metrics.ts";

/**
 * The server notice and the toast (ADR-078; the design's `components/feedback/ServerNotice.jsx` and `Toast.jsx`,
 * a visual reference). The server notice is one strip, in the HUD frame, above the entry screens' window and at
 * the top of the world in the last minutes before maintenance: an 8 px glyph and a title in the state's colour, one
 * line of running text under the title, an action at its end, centred on the strip's height as the design centres it
 * and placed there by measurement, on whole logical pixels however the text wraps (CSS centring leaves it half a pixel
 * off whenever the height left over is odd), when there is one, and a 2 px edge down its left side
 * in the state's colour, so the colour is never the only sign (the glyph and the words say it too). It stays until
 * the state changes; it is a polite status, never a toast. The toast is 128 wide in the notice frame, a glyph and a
 * title over a caption line, for a passing event such as a maintenance warning or a level reached (P2.4-T4); it takes
 * no pointer. Neither writes words of its own.
 */

/**
 * The server's states a notice shows, each with its glyph and colour.
 */
export type ServerNoticeTone = "offline" | "busy" | "soon" | "maintenance" | "restored";

/**
 * Each state's glyph and colour role: offline the broken link in danger, busy the hourglass in warning, maintenance
 * soon and under way the clock in info, and back the check in positive.
 */
export const SERVER_NOTICE_TONES: Readonly<Record<ServerNoticeTone, { readonly glyph: GlyphName; readonly colour: string }>> = {
  offline: { glyph: "unlink", colour: "var(--ot-danger)" },
  busy: { glyph: "hourglass", colour: "var(--ot-warning)" },
  soon: { glyph: "clock", colour: "var(--ot-info)" },
  maintenance: { glyph: "clock", colour: "var(--ot-info)" },
  restored: { glyph: "check", colour: "var(--ot-positive)" },
};

/**
 * A toast's tones: the server's states, and a gain such as a level reached, the rising line in positive (the design's
 * `Toast` `positive` with its `trending-up` icon), a discovery, the folded map in info (the design's `Discovery`
 * notice, its `info` tone with the `map` icon), and a full bag, the grid in danger (the design's `loot-notices` card,
 * its `full` state: `danger` with the `grid` icon).
 */
export type ToastTone = ServerNoticeTone | "gain" | "discovery" | "full";

/**
 * Each toast tone's glyph and colour role.
 */
export const TOAST_TONES: Readonly<Record<ToastTone, { readonly glyph: GlyphName; readonly colour: string }>> = {
  ...SERVER_NOTICE_TONES,
  gain: { glyph: "trending-up", colour: "var(--ot-positive)" },
  discovery: { glyph: "map", colour: "var(--ot-info)" },
  full: { glyph: "grid", colour: "var(--ot-danger)" },
};

/**
 * The strip's and the toast's measurements in logical pixels: the strip's padding (4 down, 5 across), its gaps,
 * and the edge's width; the toast's width.
 */
export const SERVER_NOTICE_METRICS = { paddingY: 4, paddingX: 5, gap: 4, lineGap: 1, edge: 2, toastWidth: 128 } as const;

/**
 * How far a glyph drops to sit centred on the title's line: 2 for an 8-pixel glyph on the label's 12, none for a
 * 12-pixel one, which fills the line as the design's 12-pixel toast icon does.
 *
 * @param glyph The glyph.
 * @returns The drop in logical pixels.
 */
export function noticeGlyphTop(glyph: GlyphName): number {
  return centreOffset(pixelType("label").lineHeight, GLYPH_SIZES[glyph]);
}

/**
 * The glyph and the words both surfaces share: the glyph in the tone's colour, then the title in the tone's colour
 * over the text, and under them, on a toast that names one, a gain such as "+50 XP" in the positive colour, drawn as
 * the design's gain toast draws its amount.
 *
 * @param props The tone's glyph and colour, colour `art` drawn untinted in the glyph's place when given (its box's room,
 *   centred on the title's line as a glyph of that size is), the title, the text, the gain and the classes' prefix.
 * @returns The content.
 */
function NoticeBody({ glyph, art, colour, title, text, gain, block }: { readonly glyph: GlyphName; readonly art?: IconArt | null | undefined; readonly colour: string; readonly title: string; readonly text: string | undefined; readonly gain?: string | undefined; readonly block: string }): ReactElement {
  const drop = art === undefined || art === null ? noticeGlyphTop(glyph) : centreOffset(pixelType("label").lineHeight, art.box);
  return (
    <>
      <span className={`${block}__glyph`} style={{ color: colour, marginTop: logical(drop) }}>
        {art === undefined || art === null ? <Glyph name={glyph} /> : <ArtIcon art={art} room={art.box} at={null} />}
      </span>
      <span className={`${block}__words`} style={{ gap: logical(SERVER_NOTICE_METRICS.lineGap) }}>
        <span className={`${block}__title`} data-kit="server-notice-title" style={{ color: colour }}>
          {title}
        </span>
        {text === undefined ? null : (
          <span className={`${block}__text`} data-kit="server-notice-text">
            {text}
          </span>
        )}
        {gain === undefined ? null : (
          <span className={`${block}__gain`} data-kit="toast-gain">
            {gain}
          </span>
        )}
      </span>
    </>
  );
}

/**
 * The strip's action in the middle of the strip's height: an area as tall as the strip's content holding the action,
 * which sits `floor((area - action) / 2)` logical pixels below the area's top and is placed again whenever either
 * changes size. Until it is measured, and in static markup, the action sits at the area's top.
 *
 * @param props The action.
 * @returns The area holding the action.
 */
function NoticeAction({ children }: { readonly children: ReactNode }): ReactElement {
  const area = useRef<HTMLSpanElement | null>(null);
  const box = useRef<HTMLSpanElement | null>(null);
  const place = useCentred(area, box, { x: false, y: true }, null);
  return (
    <span ref={area} className="ot-server-notice__action-area">
      <span ref={box} className="ot-server-notice__action" style={{ top: logical(place.top) }}>
        {children}
      </span>
    </span>
  );
}

/**
 * The server notice.
 *
 * @param props The `tone`; the `title`; the `text` under it, when there is one; an `action` at its end, such as a
 *   link or a button; and its `width` in logical pixels, the window's it sits over.
 * @returns The strip.
 * @example
 * <ServerNotice tone="offline" title="The server is offline." text="Log in opens again when it is back." width={184} />
 */
export function ServerNotice({ tone, title, text, action, width }: { readonly tone: ServerNoticeTone; readonly title: string; readonly text?: string; readonly action?: ReactNode; readonly width: number }): ReactElement {
  const metrics = SERVER_NOTICE_METRICS;
  return (
    <div
      className="ot-server-notice ot-ui-frame-hud"
      role="status"
      aria-live="polite"
      data-kit="server-notice"
      data-tone={tone}
      style={{ width: logical(width), gap: logical(metrics.gap), padding: `${logical(metrics.paddingY)} ${logical(metrics.paddingX)}` }}
    >
      <span className="ot-server-notice__edge" aria-hidden="true" style={{ width: logical(metrics.edge), backgroundColor: SERVER_NOTICE_TONES[tone].colour }} />
      <NoticeBody {...SERVER_NOTICE_TONES[tone]} title={title} text={text} block="ot-server-notice" />
      {action === undefined || action === null ? null : <NoticeAction>{action}</NoticeAction>}
    </div>
  );
}

/**
 * A toast: a passing notice in the toast column, which takes no pointer.
 *
 * @param props The `tone`, the `title`, the `text` under it, when there is one, a `gain` under that, when the
 *   notice brought one, and a `glyph` to draw in the tone's
 *   colour in place of the tone's own, such as the glyph of an item gained (the design's loot toast, whose icon is the
 *   item's). A glyph taller than the title's line is centred on it and rises above it. Colour `art` (ADR-111 decision
 *   15), when given, is drawn in the glyph's place, untinted.
 * @returns The toast.
 * @example
 * <Toast tone="soon" title="Maintenance at 14:00." text="In 30 min, for about 2 hours." />
 * <Toast tone="gain" title="Level 3" />
 * <Toast tone="gain" glyph="drop" title="Minor Healing Potion x3" />
 * <Toast tone="discovery" title="Shrine Discovered" text="You have discovered Old Shrine." gain="+50 XP" />
 */
export function Toast({ tone, title, text, gain, glyph, art }: { readonly tone: ToastTone; readonly title: string; readonly text?: string | undefined; readonly gain?: string | undefined; readonly glyph?: GlyphName | undefined; readonly art?: IconArt | null | undefined }): ReactElement {
  const metrics = SERVER_NOTICE_METRICS;
  return (
    <div className="ot-toast ot-ui-frame-notice" role="status" aria-live="polite" data-kit="toast" data-tone={tone} style={{ width: logical(metrics.toastWidth), gap: logical(metrics.gap), padding: `${logical(metrics.paddingY)} ${logical(metrics.paddingX)}` }}>
      <NoticeBody glyph={glyph ?? TOAST_TONES[tone].glyph} art={art} colour={TOAST_TONES[tone].colour} title={title} text={text} gain={gain} block="ot-toast" />
    </div>
  );
}
